import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useContext } from 'react';
import { ArrowLeft } from 'react-feather';
import { BlogPost } from '../../backend/db/all-entities';
import { CoreUIContext } from '../../react-components/core-ui/provider';
import FallbackPage from '../../react-components/fallback-page';
import Layout from '../../react-components/layout';
import MarkdownViewer from '../../react-components/markdown-viewer';
import request, { HttpError } from '../../utils/make-api-request';

interface Props {
  post: BlogPost | null;
}

const BlogPostPage: NextPage<Props> = ({ post }) => {
  const router = useRouter();
  const { showNotification } = useContext(CoreUIContext);
  if (router.isFallback) {
    return <FallbackPage title='Blog - FitSociety' />;
  }
  return (
    <Layout>
      {post != null ? (
        <>
          <Head>
            <title>{post.title} - FitSociety</title>
          </Head>
          <div className='flex justify-between text-sm select-none leading-none mb-2'>
            <Link href='/blog' as='/blog'>
              <a>
                <ArrowLeft size='1rem' className='inline align-text-bottom mr-1' />
                Blog
              </a>
            </Link>
            <span className='text-gray-400 font-medium'>Posted {new Date(post.dateCreated).toLocaleDateString()}</span>
          </div>
          <h1>{post.title}</h1>
          <article>
            <MarkdownViewer content={post.content} />
          </article>
          <section className='text-sm mt-4 text-center'>
            Like what you read?{' '}
            <button
              className='tertiary'
              onClick={() => {
                const shareData: ShareData = {
                  title: post.title,
                  url: window.location.href
                };
                if (navigator.canShare && navigator.canShare(shareData)) {
                  navigator.share(shareData);
                } else {
                  const showError = () =>
                    showNotification({
                      title: 'Failed to copy message',
                      message: "Copy from your browser's URL bar instead.",
                      style: 'danger'
                    });
                  try {
                    navigator.clipboard
                      .writeText(window.location.href)
                      .then(() => showNotification({ message: 'URL copied to clipboard', style: 'success' }))
                      .catch(showError);
                  } catch {
                    showError();
                  }
                }
              }}
            >
              Share this article
            </button>
          </section>
        </>
      ) : (
        <>
          <Head>
            <title>Blog Post Not Found</title>
          </Head>
          <div className='text-center'>
            The blog post you&rsquo;re looking for doesn&rsquo;t exist or has been deleted.
          </div>
        </>
      )}
    </Layout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: [],
  fallback: true
});

export const getStaticProps: GetStaticProps<Props> = async ctx => {
  return request<'get', BlogPost>(`blog-posts/${ctx.params!.id}`, 'get')
    .catch(e => (e instanceof HttpError && e.status === 404 ? null : Promise.reject(e)))
    .then(post => ({ props: { post }, revalidate: 60 }));
};

export default BlogPostPage;
